import React from 'react';
import styled from 'styled-components';
import { AlertContent, AlertType, Box, style, Text } from '@do/walrus';
import accountUnsecureImage from '../assets/account-unsecured.png';
import { SimpleHeader } from './Header';
import { useLocation } from '@do/seashell/navigation';

const Container = styled.div`
  display: grid;
  width: 100%;
  height: calc(100vh - ${style.vars.space['10']});
  place-items: center;
  padding: ${style.vars.space['5']} 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  h4,
  p {
    text-align: center;
  }
`;

type SSOErrorCode =
  | 'sso_error_config_missing'
  | 'sso_error_disabled'
  | 'sso_error_user_not_on_team'
  | 'sso_error_role_not_found'
  | 'sso_error_email_mismatch'
  | 'sso_error_internal'
  | 'sso_error_bad_request'
  | 'sso_error_claim_email_missing'
  | 'sso_error_claim_name_missing'
  | 'sso_error_claim_team_role_missing'
  | 'sso_error_oidc_provider'
  | 'sso_error_email_not_verified'
  | 'sso_error_single_team_membership_required'
  | 'unknown';

type SSOErrorInfo = {
  title: string;
  message: string;
  action: string;
};

const errorMap: Record<SSOErrorCode, SSOErrorInfo> = {
  sso_error_config_missing: {
    title: 'This team is not configured for SSO',
    message: 'If you believe this is an error, please contact support.',
    action: 'Please contact support.',
  },
  sso_error_disabled: {
    title: 'Single sign-on disabled',
    message: 'Authentication via SSO is currently disabled for this team.',
    action: 'Please contact your team administrator.',
  },
  sso_error_user_not_on_team: {
    title: 'User is not on team',
    message: 'The user is not associated with this team.',
    action: 'Please contact your team administrator to invite the user to the team.',
  },
  sso_error_role_not_found: {
    title: 'Role not found',
    message: 'The specified team_role assigned to this user could not be found.',
    action: 'Please contact your team administrator to assign a valid role in your IdP.',
  },
  sso_error_email_mismatch: {
    title: 'Identity email does not match',
    message: 'The email address for this user does not match the email of the linked user on this team.',
    action: 'Please contact your team administrator to update the email address in your IdP.',
  },
  sso_error_internal: {
    title: 'Internal Error',
    message: 'An internal error occurred during authentication.',
    action: 'Please try again or contact support.',
  },
  // IGNORE, this should really be internal error, will be removed in future
  sso_error_bad_request: {
    title: 'Internal Error',
    message: 'An internal error occurred during authentication.',
    action: 'Please try again or contact support.',
  },
  sso_error_claim_email_missing: {
    title: 'Required claim missing',
    message: 'The email claim is missing from the identity provider response.',
    action: 'Please contact your team administrator to update your IdP to send the necessary claims.',
  },
  sso_error_claim_name_missing: {
    title: 'Required claim missing',
    message: 'The name claim is missing from the identity provider response.',
    action: 'Please contact your team administrator to update your IdP to send the necessary claims.',
  },
  sso_error_claim_team_role_missing: {
    title: 'Required claim missing',
    message: 'The team_role claim is missing from the identity provider response.',
    action: 'Please contact your team administrator to update your IdP to send the necessary claims.',
  },
  sso_error_oidc_provider: {
    title: 'OIDC provider error',
    message:
      'An error occurred while attempting to reach the OIDC provider. This could be due to a misconfiguration of provider URL or missing OIDC discovery endpoint.',
    action: 'Please contact your team administrator.',
  },
  sso_error_email_not_verified: {
    title: 'Email not verified',
    message: 'The email address associated with your account is not verified.',
    action: 'Please verify your email address before attempting to authenticate.',
  },
  sso_error_single_team_membership_required: {
    title: 'Cannot be a member of other teams',
    message: 'This team requires single team membership.',
    action: 'Please contact your team administrator or leave your other team(s) to continue.',
  },
  unknown: {
    title: 'Unknown error',
    message:
      'An unknown error occurred while authenticating. If you are unable to authenticate through your IdP, please contact your team administrator.',
    action: 'Please try again or contact support.',
  },
};

export const SSOErrorPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestId = params.get('request_id') ?? '';
  const errorParam = params.get('code') ?? 'unknown';
  const error = errorMap[errorParam as SSOErrorCode] ?? errorMap.unknown;

  return (
    <>
      <SimpleHeader />
      <Container>
        <Box maxWidth="800px" ml={3} mr={3} p={6}>
          <Content>
            <img
              src={accountUnsecureImage}
              alt="A user profile icon with a red warning sign indicating an issue with authentication"
            />
            <Text type="h4" data-testid="sso-error-title">
              {error.title}
            </Text>
            <Text type="p" data-testid="sso-error-message">
              {error.message}
            </Text>
            <div data-testid="sso-error-action">
              <AlertContent type={AlertType.ERROR}>{error.action}</AlertContent>
            </div>
            {requestId && (
              <Text type="small" tertiary data-testid="sso-error-request-id">
                <strong>Request ID:</strong> {requestId}
              </Text>
            )}
          </Content>
        </Box>
      </Container>
    </>
  );
};
